import { useNavigate } from "@tanstack/react-router";
import { Fragment, type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useFocusTrap } from "~/components/a11y/focus-manager";
import { Icon } from "~/components/ui/icon";
import { useI18n } from "~/i18n";
import type { OperatorHit } from "~/lib/domain/operator-search";
import { useOperatorSearchState } from "~/lib/search/use-operator-search";
import { type DestinationIconName, NavIcon } from "./nav-icon";

export type CommandDestination = {
  group: string;
  label: string;
  to: string;
  icon?: DestinationIconName;
};

/**
 * Return the platform's conventional command hint without relying on the
 * browser's locale or keyboard layout. Keep this helper pure when arguments
 * are supplied so the same rule can be verified in tests and reused by the
 * shell's trigger. ⌘K / Ctrl+K is the hint; ⌘S / Ctrl+S remains a silent
 * alias for one release so muscle memory keeps working.
 */
export function getCommandShortcut(platform?: string, userAgent?: string): "⌘K" | "Ctrl+K" {
  const resolvedPlatform = platform ?? (typeof navigator !== "undefined" ? navigator.platform : "");
  const resolvedUserAgent =
    userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  return /Mac|iPhone|iPad|iPod/i.test(`${resolvedPlatform} ${resolvedUserAgent}`) ? "⌘K" : "Ctrl+K";
}

/** ⌘K / Ctrl+K opens search. ⌘S / Ctrl+S is kept as an alias for one release. */
export function isCommandSearchShortcut(event: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): boolean {
  if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return false;
  const key = event.key.toLowerCase();
  return key === "k" || key === "s";
}

export function CommandSearch({
  open,
  destinations,
  onClose,
}: {
  open: boolean;
  destinations: CommandDestination[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const search = useOperatorSearchState({ open, query, destinations });
  const results = search.hits;
  const loadingGroups = search.categories
    .filter((category) => category.state === "loading")
    .map((category) => category.group);
  const failedGroups = search.failed.map((category) => category.group);
  const deniedGroups = [...new Set(search.denied.map((category) => category.group))];

  useFocusTrap(dialogRef, open, onClose);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (activeIndex < results.length) return;
    setActiveIndex(Math.max(0, results.length - 1));
  }, [activeIndex, results.length]);

  useEffect(() => {
    if (!open || results.length === 0) return;
    dialogRef.current
      ?.querySelector<HTMLElement>(`#command-search-result-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, results.length]);

  if (!open) return null;

  const select = (hit: OperatorHit) => {
    onClose();
    void navigate({
      to: hit.to as never,
      params: (hit.params ?? {}) as never,
      search: (hit.search ?? {}) as never,
    });
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => (results.length === 0 ? 0 : (index + 1) % results.length));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        results.length === 0 ? 0 : (index - 1 + results.length) % results.length,
      );
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setActiveIndex(Math.max(0, results.length - 1));
      return;
    }
    if (event.key === "Enter") {
      const active = results[activeIndex];
      if (!active) return;
      event.preventDefault();
      select(active);
    }
  };

  const activeDestination = results[activeIndex];

  return (
    <>
      <button
        type="button"
        className="command-search__scrim"
        aria-label={t("commandSearch.close")}
        onClick={onClose}
        tabIndex={-1}
      />
      <dialog
        open
        id="command-search-dialog"
        className="command-search"
        aria-modal="true"
        aria-labelledby="command-search-title"
        aria-describedby="command-search-description"
        ref={dialogRef}
      >
        <strong id="command-search-title" className="sr-only">
          {t("commandSearch.title")}
        </strong>
        <span id="command-search-description" className="sr-only">
          {t("commandSearch.description")}
        </span>

        <div className="command-search__input-wrap">
          <SearchGlyph />
          <input
            ref={inputRef}
            className="command-search__input"
            type="search"
            aria-labelledby="command-search-title"
            aria-describedby="command-search-description"
            placeholder={t("commandSearch.placeholder")}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleInputKeyDown}
          />
          <button type="button" className="command-search__close" onClick={onClose}>
            <span aria-hidden="true">Esc</span>
            <span className="sr-only">{t("commandSearch.close")}</span>
          </button>
        </div>

        <span className="sr-only" aria-live="polite">
          {activeDestination
            ? `${activeDestination.label}, ${activeDestination.group}`
            : search.searching
              ? t("commandSearch.searching", { groups: loadingGroups.join(", ") })
              : t("commandSearch.emptyTitle")}
        </span>

        {search.searching || failedGroups.length > 0 ? (
          <div
            className="command-search__status"
            data-state={failedGroups.length ? "error" : "loading"}
          >
            {search.searching ? (
              <span className="command-search__status-line" data-tone="loading">
                <Icon name="refresh" size={16} className="command-search__spinner" />
                {t("commandSearch.searching", { groups: loadingGroups.join(", ") })}
              </span>
            ) : null}
            {failedGroups.length > 0 ? (
              <span className="command-search__status-line" data-tone="error" role="alert">
                <Icon name="warning" size={16} />
                {t("commandSearch.partialError", { groups: failedGroups.join(", ") })}
                <button
                  type="button"
                  className="command-search__retry"
                  onClick={() => search.retryFailed()}
                >
                  {t("common.retry")}
                </button>
              </span>
            ) : null}
          </div>
        ) : null}

        <ul
          className="command-search__results scroll-thin"
          id="command-search-results"
          aria-label={t("commandSearch.results")}
        >
          {results.length === 0 && search.searching ? (
            <li aria-hidden="true">
              <div className="command-search__loading">
                {[62, 48, 70, 54].map((width) => (
                  <span className="command-search__loading-row" key={width}>
                    <span className="skeleton command-search__loading-icon" />
                    <span className="command-search__loading-copy">
                      <span className="skeleton" style={{ height: 13, width: `${width}%` }} />
                      <span className="skeleton" style={{ height: 11, width: `${width - 20}%` }} />
                    </span>
                  </span>
                ))}
              </div>
            </li>
          ) : results.length === 0 ? (
            <li>
              <div className="command-search__empty">
                <SearchGlyph />
                <strong>
                  {failedGroups.length > 0
                    ? t("commandSearch.emptyPartialTitle")
                    : t("commandSearch.emptyTitle")}
                </strong>
                <span>{t("commandSearch.emptyBody")}</span>
              </div>
            </li>
          ) : (
            results.map((hit, index) => {
              const previous = results[index - 1];
              const showGroup = hit.group !== previous?.group;
              return (
                <Fragment key={hit.id}>
                  {showGroup ? <li className="command-search__group">{hit.group}</li> : null}
                  <li>
                    <button
                      type="button"
                      id={`command-search-result-${index}`}
                      className="command-search__result"
                      data-kind={hit.kind}
                      data-active={index === activeIndex ? "true" : "false"}
                      onMouseEnter={() => setActiveIndex(index)}
                      onFocus={() => setActiveIndex(index)}
                      onClick={() => select(hit)}
                    >
                      <span
                        className="command-search__result-icon"
                        data-kind={hit.kind}
                        aria-hidden="true"
                      >
                        <ResultIcon hit={hit} />
                      </span>
                      <span className="command-search__result-copy">
                        <strong>{hit.label}</strong>
                        {hit.hint ? <small>{hit.hint}</small> : null}
                      </span>
                      <span className="command-search__result-path mono">
                        {hit.kind === "catalog"
                          ? (formatSearchNcm(hit.search?.ncm) ??
                            (hit.chapter
                              ? t("rules.chapter.short", { chapter: hit.chapter })
                              : resolvedPath(hit)))
                          : resolvedPath(hit)}
                      </span>
                      <span className="command-search__result-enter" aria-hidden="true">
                        <Icon name="arrowRight" size={16} />
                      </span>
                    </button>
                  </li>
                </Fragment>
              );
            })
          )}
        </ul>

        {deniedGroups.length > 0 && query.trim().length >= 2 ? (
          <p className="command-search__denied">
            <Icon name="lock" size={16} />
            {t("commandSearch.denied", { groups: deniedGroups.join(", ") })}
          </p>
        ) : null}

        <div className="command-search__footer" aria-hidden="true">
          <span className="command-search__honesty">{t("commandSearch.honesty")}</span>
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd>
            {t("commandSearch.move")}
          </span>
          <span>
            <kbd>↵</kbd>
            {t("commandSearch.open")}
          </span>
          <span>
            <kbd>Esc</kbd>
            {t("commandSearch.close")}
          </span>
        </div>
      </dialog>
    </>
  );
}

function formatSearchNcm(ncm: string | undefined): string | undefined {
  if (!ncm) return undefined;
  return [ncm.slice(0, 4), ncm.slice(4, 6), ncm.slice(6, 8)].filter(Boolean).join(".");
}

export function SearchGlyph() {
  return <Icon name="search" />;
}

function ResultIcon({ hit }: { hit: OperatorHit }): ReactNode {
  if (hit.kind === "catalog") {
    return hit.chapter ?? hit.search?.ncm?.slice(0, 2) ?? "SH";
  }
  if (hit.kind === "launch") {
    return <SearchGlyph />;
  }
  if (hit.icon) {
    return <NavIcon name={hit.icon} />;
  }
  return <SearchGlyph />;
}

/**
 * The route hint shows where Enter goes, not the router template: `$param`
 * segments are filled in, and long ids are shortened to their first 8 chars.
 */
function resolvedPath(hit: OperatorHit): string {
  return hit.to.replace(/\$([A-Za-z]+)/g, (segment, name: string) => {
    const value = hit.params?.[name];
    if (!value) return segment;
    return value.length > 12 ? `${value.slice(0, 8)}…` : value;
  });
}
